import { formatWeight } from "../../../utils/numberUtils";

export type ExclusionExplainerMode = "none" | "excluded";

export type ExclusionExplainerPhase = "start" | "plan" | "filter" | "complete";

export type ExclusionTradeAction = "none" | "sell" | "buy" | "skipped" | "sold" | "bought";

export interface ExclusionStockSnapshot {
  symbol: string;
  dollars: number;
  currentPct: number;
  targetPct: number;
  excluded: boolean;
  tradeAction: ExclusionTradeAction;
}

export interface ExclusionExplainerSnapshot {
  nlv: number;
  stocks: ExclusionStockSnapshot[];
  headline: string | null;
}

export const EXCLUSION_EXPLAINER_NLV = 90_000;
export const EXCLUSION_TARGET_PCT = 33.33;
export const EXCLUSION_SYMBOL = "AAPL";

export const EXCLUSION_PHASE_ORDER: ExclusionExplainerPhase[] = [
  "start",
  "plan",
  "filter",
  "complete",
];

export const EXCLUSION_PHASE_LABELS: Record<ExclusionExplainerPhase, string> = {
  start: "Strategy drifted",
  plan: "Rebalance planned",
  filter: "Exclusion applies",
  complete: "Outcome",
};

const START: ExclusionStockSnapshot[] = [
  { symbol: "AAPL", dollars: 40_500, currentPct: 45, targetPct: EXCLUSION_TARGET_PCT, excluded: false, tradeAction: "none" },
  { symbol: "MSFT", dollars: 27_000, currentPct: 30, targetPct: EXCLUSION_TARGET_PCT, excluded: false, tradeAction: "none" },
  { symbol: "GOOG", dollars: 22_500, currentPct: 25, targetPct: EXCLUSION_TARGET_PCT, excluded: false, tradeAction: "none" },
];

const COMPLETE_NONE: ExclusionStockSnapshot[] = [
  { symbol: "AAPL", dollars: 30_000, currentPct: EXCLUSION_TARGET_PCT, targetPct: EXCLUSION_TARGET_PCT, excluded: false, tradeAction: "sold" },
  { symbol: "MSFT", dollars: 30_000, currentPct: EXCLUSION_TARGET_PCT, targetPct: EXCLUSION_TARGET_PCT, excluded: false, tradeAction: "bought" },
  { symbol: "GOOG", dollars: 30_000, currentPct: EXCLUSION_TARGET_PCT, targetPct: EXCLUSION_TARGET_PCT, excluded: false, tradeAction: "bought" },
];

const COMPLETE_EXCLUDED: ExclusionStockSnapshot[] = [
  { symbol: "AAPL", dollars: 40_500, currentPct: 45, targetPct: EXCLUSION_TARGET_PCT, excluded: true, tradeAction: "skipped" },
  { symbol: "MSFT", dollars: 29_250, currentPct: 32.5, targetPct: EXCLUSION_TARGET_PCT, excluded: false, tradeAction: "bought" },
  { symbol: "GOOG", dollars: 20_250, currentPct: 22.5, targetPct: EXCLUSION_TARGET_PCT, excluded: false, tradeAction: "sold" },
];

function withTradePlan(excluded: boolean): ExclusionStockSnapshot[] {
  return START.map((stock) => {
    if (stock.symbol === EXCLUSION_SYMBOL) {
      return {
        ...stock,
        excluded,
        tradeAction: excluded ? "skipped" : "sell",
      };
    }

    return {
      ...stock,
      excluded: false,
      tradeAction: "buy",
    };
  });
}

export function getExclusionExplainerSnapshot(
  mode: ExclusionExplainerMode,
  phase: ExclusionExplainerPhase,
): ExclusionExplainerSnapshot {
  if (phase === "start") {
    return {
      nlv: EXCLUSION_EXPLAINER_NLV,
      stocks: START,
      headline: "AAPL is overweight versus its one-third target.",
    };
  }

  if (phase === "plan") {
    return {
      nlv: EXCLUSION_EXPLAINER_NLV,
      stocks: withTradePlan(false),
      headline: "A rebalance would sell AAPL and buy MSFT and GOOG.",
    };
  }

  if (phase === "filter") {
    const excluded = mode === "excluded";
    return {
      nlv: EXCLUSION_EXPLAINER_NLV,
      stocks: withTradePlan(excluded),
      headline: excluded
        ? "AAPL is on your exclusion list, so its planned trade is skipped."
        : "No exclusions — all planned trades would run.",
    };
  }

  return {
    nlv: EXCLUSION_EXPLAINER_NLV,
    stocks: mode === "excluded" ? COMPLETE_EXCLUDED : COMPLETE_NONE,
    headline: mode === "excluded"
      ? "AAPL stayed unchanged; the other positions were adjusted."
      : "All three positions moved back toward one-third each.",
  };
}

export function formatExclusionTradeLabel(action: ExclusionTradeAction): string {
  switch (action) {
    case "sell":
      return "Would sell";
    case "buy":
      return "Would buy";
    case "sold":
      return "Sold";
    case "bought":
      return "Bought";
    case "skipped":
      return "Skipped";
    default:
      return "No trade";
  }
}

export function formatTargetPct(pct: number): string {
  return `${formatWeight(pct, pct % 1 === 0 ? 0 : 1)}%`;
}
